import { useCallback, useEffect, useMemo, useState } from 'react';

import { Form, Input, notification, Select, TreeSelect } from 'antd';

import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import {
  DOMAIN_HIERARCHY_LIMIT,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  FORM_RULES,
  MODAL_WIDTHS,
  PLACEHOLDER_MESSAGES,
  VALIDATION_MESSAGES,
  VALIDATION_RULES,
} from '@/shared/lib/constants';
import { transformDomainHierarchyToTreeSelect } from '@/shared/lib/tree';
import { areArraysEqual, transformOwnersToSelectOptions } from '@/shared/lib/utils';
import type { JsonPatchOperation } from '@/shared/types/common';
import { BaseModal } from '@/shared/ui/BaseModal';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { useDomainHierarchy } from '@/entities/domain';
import { useUserOwners } from '@/entities/owner';
import type { TagDetail } from '@/entities/tags/domain';

import type { CreateTagRequest } from '@/features/tags/api';
import { useCreateTag, useUpdateTag } from '@/features/tags/model';
import styles from '@/features/tags/ui/_internal/modal.module.css';

type FormValues = {
  name: string;
  displayName?: string;
  description: string;
  owners?: string[];
  domains?: string;
};

type ParentInfo = {
  classification: string;
  classificationName: string;
};

type TagFormModalProps = {
  open: boolean;
  mode?: 'create' | 'edit';
  parentInfo?: ParentInfo;
  tagData?: TagDetail;
  tagFqn?: string;
  onClose: () => void;
  onSuccess?: (parentFqn: string) => void;
};

export const TagFormModal = ({
  open,
  mode = 'create',
  parentInfo,
  tagData,
  tagFqn,
  onClose,
  onSuccess,
}: TagFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [domainSearchValue, setDomainSearchValue] = useState('');
  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag(tagFqn);
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const { validateLength: validateDomainSearch } = useSearchLengthValidation({
    maxLength: 30,
    warningMessage: VALIDATION_MESSAGES.DOMAIN_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
    shouldTruncate: true, // TreeSelect는 maxLength prop이 동작하지 않으므로 수동 truncate
  });

  const { data: domainHierarchy } = useDomainHierarchy({ limit: DOMAIN_HIERARCHY_LIMIT });
  const { data: usersData } = useUserOwners();

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const isEditMode = mode === 'edit';

  const userOptions = useMemo(() => transformOwnersToSelectOptions(usersData), [usersData]);

  const modalTitle = useMemo(() => {
    if (isEditMode) {
      return `태그 수정 (${tagData?.displayName || tagData?.name || ''})`;
    }
    if (parentInfo?.classificationName) {
      return `태그 추가 (${parentInfo.classificationName})`;
    }
    return '태그 추가';
  }, [isEditMode, tagData, parentInfo]);

  const domainTreeData = useMemo(() => {
    if (!domainHierarchy?.data) return [];
    return transformDomainHierarchyToTreeSelect(domainHierarchy.data);
  }, [domainHierarchy]);

  useEffect(() => {
    if (open) {
      if (isEditMode && tagData) {
        form.setFieldsValue({
          name: tagData.name,
          displayName: tagData.displayName || '',
          description: tagData.description || '',
          owners: tagData.owners?.map((o) => o.id) || [],
          domains: tagData.domains?.[0]?.fullyQualifiedName,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, form, isEditMode, tagData]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const generatePatchOperations = useCallback(
    (values: FormValues): JsonPatchOperation[] => {
      if (!tagData) return [];

      const operations: JsonPatchOperation[] = [];

      if (values.displayName !== (tagData.displayName || '')) {
        if (values.displayName) {
          operations.push({
            op: tagData.displayName ? 'replace' : 'add',
            path: '/displayName',
            value: values.displayName,
          });
        } else if (tagData.displayName) {
          operations.push({ op: 'remove', path: '/displayName' });
        }
      }

      if (values.description !== (tagData.description || '')) {
        if (values.description) {
          operations.push({
            op: tagData.description ? 'replace' : 'add',
            path: '/description',
            value: values.description,
          });
        }
      }

      const currentOwners = tagData.owners?.map((o) => o.id) || [];
      const newOwners = values.owners || [];
      if (!areArraysEqual(currentOwners, newOwners)) {
        const ownerValues = newOwners.map((id) => ({ id, type: 'user' }));
        operations.push({
          op: currentOwners.length > 0 ? 'replace' : 'add',
          path: '/owners',
          value: ownerValues,
        });
      }

      const currentDomain = tagData.domains?.[0]?.fullyQualifiedName || '';
      const newDomain = values.domains || '';
      if (currentDomain !== newDomain) {
        if (newDomain) {
          operations.push({
            op: currentDomain ? 'replace' : 'add',
            path: '/domains',
            value: [{ type: 'domain', fullyQualifiedName: newDomain }],
          });
        } else if (currentDomain) {
          operations.push({ op: 'remove', path: '/domains' });
        }
      }

      return operations;
    },
    [tagData]
  );

  const transformFormValuesToRequest = useCallback(
    (values: FormValues): CreateTagRequest => {
      if (!parentInfo) {
        throw new Error('parentInfo is required for create mode');
      }

      const domains: string[] = values.domains ? [values.domains] : [];
      const owners = (values.owners || []).map((id) => ({ id, type: 'user' as const }));

      const request: CreateTagRequest = {
        classification: parentInfo.classification,
        name: values.name,
        displayName: values.displayName || undefined,
        description: values.description || undefined,
        domains,
        owners,
      };

      return request;
    },
    [parentInfo]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (isEditMode && tagData) {
          const operations = generatePatchOperations(values);
          if (operations.length === 0) {
            notification.info({
              message: '변경 사항 없음',
              description: '수정된 내용이 없습니다.',
              placement: 'bottomLeft',
              duration: 3,
            });
            handleClose();
            return;
          }
          await updateMutation.mutateAsync({
            tagId: tagData.id,
            operations,
          });
          handleClose();

          if (tagFqn) {
            const classification = tagFqn.split('.')[0];
            onSuccess?.(classification);
          }
        } else {
          const request = transformFormValuesToRequest(values);
          await createMutation.mutateAsync(request);
          handleClose();

          const refreshFqn = parentInfo?.classification || '';
          if (refreshFqn) {
            onSuccess?.(refreshFqn);
          }
        }
      } catch (error) {
        console.error('Failed to submit tag form:', error);
      }
    },
    [
      isEditMode,
      tagData,
      generatePatchOperations,
      updateMutation,
      tagFqn,
      handleClose,
      onSuccess,
      transformFormValuesToRequest,
      createMutation,
      parentInfo?.classification,
    ]
  );

  return (
    <BaseModal
      title={modalTitle}
      open={open}
      onClose={handleClose}
      width={MODAL_WIDTHS.STANDARD_FORM}
      onOk={() => form.submit()}
      okText={isEditMode ? '저장' : '생성'}
      confirmLoading={isLoading}
      styles={{ body: { maxHeight: 800 } }}>
      <Form form={form} layout="vertical" onFinish={handleSubmit} className={styles.form}>
        <Form.Item
          name="name"
          label={FORM_LABELS.NAME}
          rules={[...FORM_RULES.REQUIRED_NAME, ...FORM_RULES.TAG_NAME]}
          validateFirst>
          <Input placeholder={FORM_PLACEHOLDERS.NAME} disabled={isEditMode} allowClear />
        </Form.Item>

        <Form.Item name="displayName" label={FORM_LABELS.DISPLAY_NAME}>
          <Input placeholder={FORM_PLACEHOLDERS.DISPLAY_NAME} allowClear />
        </Form.Item>

        <Form.Item
          name="description"
          label={FORM_LABELS.DESCRIPTION}
          rules={FORM_RULES.REQUIRED_DESCRIPTION}
          validateTrigger={['onSubmit']}>
          <DescriptionTextArea
            placeholder={FORM_PLACEHOLDERS.DESCRIPTION}
            showHelper
            maxLength={VALIDATION_RULES.TAG_DESCRIPTION_MAX_LENGTH}
          />
        </Form.Item>

        <Form.Item name="domains" label={FORM_LABELS.DOMAINS}>
          <TreeSelect
            placeholder={PLACEHOLDER_MESSAGES.DOMAIN_SELECT}
            allowClear
            treeData={domainTreeData}
            treeDefaultExpandAll
            treeLine
            searchValue={domainSearchValue}
            onSearch={(value) => {
              const validatedValue = validateDomainSearch(value);
              setDomainSearchValue(validatedValue);
            }}
          />
        </Form.Item>

        {showOwners && (
          <Form.Item name="owners" label={FORM_LABELS.OWNERS}>
            <Select mode="multiple" placeholder={PLACEHOLDER_MESSAGES.OWNER_SELECT} allowClear options={userOptions} />
          </Form.Item>
        )}
      </Form>
    </BaseModal>
  );
};
